import {
  STUDY_SYSTEM_PROMPT,
  INTAKE_SYSTEM_PROMPT,
  CHECKIN_SYSTEM_PROMPT,
} from '../data/promptTemplates.js';
import { authenticatedFetch } from './apiClient.js';

const COMPLETE_TOKEN = '[INTAKE_COMPLETE]';
const CHECKIN_TOKEN = '[CHECKIN_COMPLETE]';

async function callChatAPI({ messages, system, max_tokens }, retries = 2) {
  const response = await authenticatedFetch('/api/chat', {
    method: 'POST',
    body: JSON.stringify({ messages, system, max_tokens }),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {}

  // Anthropic surfaces transient capacity issues either as HTTP 529 or as
  // a 200 with { error: { type: 'overloaded_error' } }. Retry both with
  // a short backoff before giving up — most of these clear inside a few
  // seconds. After retries are exhausted throw a tagged error so callers
  // can render a friendly message instead of raw JSON.
  if (data?.error?.type === 'overloaded_error' || response.status === 529) {
    if (retries > 0) {
      console.log('[anthropic] overloaded — retrying in 3s, retries left:', retries);
      await new Promise((r) => setTimeout(r, 3000));
      return callChatAPI({ messages, system, max_tokens }, retries - 1);
    }
    const err = new Error('overloaded');
    err.status = 529;
    throw err;
  }

  if (!response.ok) {
    const errMsg =
      data?.error?.message || data?.error || `Chat API error: ${response.status}`;
    const err = new Error(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
    err.status = response.status;
    throw err;
  }

  return data;
}

function extractText(response) {
  return (response?.content || [])
    .filter((b) => b.type === 'text')
    .map((b) => b.text)
    .join('\n');
}

export async function conductIntake(messages, _userPartial, guide) {
  const system = INTAKE_SYSTEM_PROMPT({
    guideName: guide?.name || 'your guide',
    guideTone: guide?.tone || 'balanced',
  });

  const response = await callChatAPI({
    messages,
    system,
    max_tokens: 1024,
  });

  const text = extractText(response);
  const isComplete = text.includes(COMPLETE_TOKEN);
  const cleanText = text.replace(COMPLETE_TOKEN, '').trim();

  return { reply: cleanText, isComplete };
}

export async function extractProfile(messages, guide) {
  const transcript = messages
    .map(
      (m) =>
        `${m.role === 'user' ? 'USER' : (guide?.name || 'GUIDE').toUpperCase()}: ${m.content}`
    )
    .join('\n\n');

  const system = `You extract a structured user profile from a faith-formation intake conversation.
Return ONLY valid JSON — no prose, no markdown fences. Begin with { and end with }.

Schema:
{
  "name": string,
  "age": number | null,
  "gender": "male" | "female" | "",
  "enneagram_type": number | null,
  "enneagram_wing": number | null,
  "faith_background": string,
  "life_season": string,
  "primary_pain_point": string,
  "focus_areas": [string]
}

If a field was never discussed, use the empty string, an empty array, or null. Infer Enneagram type from the conversation if the user did not name one — but only when the signal is clear.`;

  const response = await callChatAPI({
    messages: [
      {
        role: 'user',
        content: `Conversation transcript:\n\n${transcript}\n\nReturn the JSON profile.`,
      },
    ],
    system,
    max_tokens: 1024,
  });

  const text = extractText(response).trim();

  const first = text.indexOf('{');
  const last = text.lastIndexOf('}');
  if (first === -1 || last === -1) {
    throw new Error('Profile extractor did not return JSON.');
  }
  const profile = JSON.parse(text.slice(first, last + 1));

  // Fix 5 — guarantee a name. If the guide failed to ask, default to "Friend".
  const rawName = (profile.name || '').trim();
  if (!rawName) {
    console.warn(
      '[Rooted] extractProfile: user name was missing from intake. Defaulting to "Friend". ' +
        'The guide likely violated the CRITICAL OPENING RULE — review INTAKE_SYSTEM_PROMPT.'
    );
    profile.name = 'Friend';
  }
  return profile;
}

function parseStudyJSON(text) {
  if (!text) throw new Error('Empty model response');
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (jsonMatch) return JSON.parse(jsonMatch[0]);
  return JSON.parse(text);
}

// generateNextDay — produce a single day of the study. weekNumber drives
// adaptive prompting so Week 2+ explicitly builds on prior weeks.
// usedContent = { scriptures, themes, pullQuotes } — strings the model
// must NOT repeat across the user's entire history.
export async function generateNextDay(
  userProfile,
  dayNumber,
  weekNumber,
  previousDays,
  dailyFeedback,
  intakeTranscript,
  usedContent = {}
) {
  void intakeTranscript;
  const wn = weekNumber || 1;

  const usedScriptures = usedContent.scriptures || [];
  const usedThemes = usedContent.themes || [];
  const usedPullQuotes = usedContent.pullQuotes || [];

  const avoidBlock =
    usedScriptures.length > 0 || usedThemes.length > 0
      ? `\n\nCRITICAL — DO NOT REPEAT ANY OF THESE:
Scriptures already used: ${usedScriptures.join(', ')}
Themes already used: ${usedThemes.join(', ')}
Pull quotes already used: ${usedPullQuotes.join(' | ')}

Choose entirely different scripture passages, themes, and angles. Every day must feel fresh and distinct from everything that came before.`
      : '';

  const weekContext =
    wn > 1
      ? `\n\nThis is Week ${wn} of the user's ongoing study. Build on themes from previous weeks. Reference growth the user has shown. Go deeper than Week ${wn - 1}.`
      : '';

  const feedbackContext =
    Array.isArray(dailyFeedback) && dailyFeedback.length > 0
      ? `\n\nDAILY CHECK-IN FEEDBACK (use this to adapt Day ${dayNumber}):\n${dailyFeedback
          .map(
            (f) =>
              `Week ${f.weekNumber || 1} Day ${f.dayNumber}: ${f.summary || f.entry || f.raw || ''}`
          )
          .join('\n')}`
      : '';

  const previousContext =
    Array.isArray(previousDays) && previousDays.length > 0
      ? `\n\nPREVIOUS DAYS ALREADY DELIVERED THIS WEEK:\n${previousDays
          .map(
            (d) =>
              `Day ${d.dayNumber || d.day_number}: "${d.title}" — ${d.scripture_reference}`
          )
          .join(
            '\n'
          )}\nDo not repeat themes, scriptures, or content from these days.`
      : '';

  const system = `You are generating Week ${wn} Day ${dayNumber} of a personalized Bible study for ${userProfile?.name || 'this person'}.
${weekContext}
${feedbackContext}
${previousContext}
${avoidBlock}

PERSON PROFILE:
- Name: ${userProfile?.name || 'friend'}
- Enneagram Type: ${userProfile?.enneagram_type || 'unknown'}w${userProfile?.enneagram_wing || ''}  (${userProfile?.enneagram_label || ''})
- Faith background: ${userProfile?.faith_background || 'Christian'}
- Life season: ${userProfile?.life_season || 'navigating a personal season'}
- Primary pain point: ${userProfile?.primary_pain_point || ''}
- Focus areas: ${(userProfile?.focus_areas || []).join(', ')}

CRITICAL INSTRUCTIONS:
- This is Week ${wn} Day ${dayNumber} of 5. Build on the arc from previous days within the week.
- If there is check-in feedback, explicitly incorporate it. Reference what the person shared. The study should feel like it heard them.
- If this is Day 2+, open the day title and subtitle with awareness of what came before.
- Target 600-900 words of content per day — 5-10 minutes of meaningful engagement.
- The enneagram_insight MUST be specific to Type ${userProfile?.enneagram_type || 2}w${userProfile?.enneagram_wing || ''}.

Return ONLY a valid JSON object for this single day with this exact structure:
{
  "dayNumber": ${dayNumber},
  "weekNumber": ${wn},
  "day_name": "Monday",
  "title": "Day title",
  "subtitle": "Day subtitle",
  "scripture_reference": "Book Chapter:Verses",
  "scripture_text": "The actual scripture text",
  "opening_prayer": "A personal opening prayer",
  "content_sections": [
    { "heading": "Section heading", "body": "Section content — 2-3 paragraphs" },
    { "heading": "Section heading", "body": "Section content — 2-3 paragraphs" },
    { "heading": "Section heading", "body": "Section content — 2-3 paragraphs" }
  ],
  "enneagram_insight": "Specific insight for this Enneagram type",
  "pull_quote": "A memorable quote or truth from today",
  "reflection_questions": ["Question 1", "Question 2", "Question 3"],
  "closing_practice": "A practical closing exercise or prayer"
}`;

  console.log(
    '[generateNextDay] Day',
    dayNumber,
    'starting at:',
    new Date().toISOString()
  );
  const startedAt = Date.now();

  try {
    const response = await callChatAPI({
      system,
      messages: [
        {
          role: 'user',
          content: `Generate Day ${dayNumber} now. Return only valid JSON.`,
        },
      ],
      max_tokens: 2048,
    });
    const text = extractText(response).trim();
    console.log(
      '[generateNextDay] Day',
      dayNumber,
      'completed in',
      Date.now() - startedAt,
      'ms'
    );
    const parsed = parseStudyJSON(text);
    // Ensure dayNumber + weekNumber are set even if the model omitted them.
    if (!parsed.dayNumber) parsed.dayNumber = dayNumber;
    if (!parsed.weekNumber) parsed.weekNumber = wn;
    return parsed;
  } catch (err) {
    const isFilterError =
      err?.message?.toLowerCase?.().includes('content filtering') ||
      err?.status === 400;
    if (!isFilterError) throw err;

    console.log(
      '[generateNextDay] W',
      wn,
      'D',
      dayNumber,
      '— retrying with sanitized profile'
    );
    const safeProfile = {
      ...userProfile,
      life_season: 'a personal growth season',
      primary_pain_point: 'growth, identity, and faith',
      focus_areas: ['grace', 'identity', 'relationships'],
    };
    const retry = await generateNextDay(
      safeProfile,
      dayNumber,
      wn,
      previousDays,
      [],
      '',
      usedContent
    );
    return retry;
  }
}

// generateStudy — Week 1 Day 1 only. Days 2-5 of Week 1 are produced
// progressively after each check-in. Week 2+ is triggered after the
// user subscribes / finishes the previous week.
export async function generateStudy(
  userProfile,
  conversationContext,
  dailyFeedback = []
) {
  const day1 = await generateNextDay(
    userProfile,
    1,
    1,
    [],
    dailyFeedback || [],
    conversationContext
  );

  return {
    week_title: `Week 1 — ${day1.title || 'Your first day'}`,
    week_theme: day1.subtitle || '',
    opening_statement: `Your first week begins here, ${userProfile?.name || 'friend'}.`,
    days: [day1],
    totalDays: 5,
  };
}

export async function conductDailyCheckin(
  dayNumber,
  dayContent,
  previousMessages,
  guide
) {
  const system = CHECKIN_SYSTEM_PROMPT({
    guideName: guide?.name || 'your guide',
    guideTone: guide?.tone || 'balanced',
    dayNumber,
    dayTitle: dayContent?.title || `Day ${dayNumber}`,
    dayScripture: dayContent?.scripture_reference || '',
  });

  const response = await callChatAPI({
    messages: previousMessages,
    system,
    max_tokens: 700,
  });

  const text = extractText(response);
  const isComplete = text.includes(CHECKIN_TOKEN);
  const cleanText = text.replace(CHECKIN_TOKEN, '').trim();

  let additionalContext = null;
  if (isComplete) {
    additionalContext = await summarizeCheckin(
      dayNumber,
      [...previousMessages, { role: 'assistant', content: cleanText }],
      guide
    );
  }

  return { reply: cleanText, isComplete, additionalContext };
}

async function summarizeCheckin(dayNumber, messages, guide) {
  const transcript = messages
    .map(
      (m) =>
        `${m.role === 'user' ? 'USER' : (guide?.name || 'GUIDE').toUpperCase()}: ${m.content}`
    )
    .join('\n\n');

  const system = `Summarize this daily check-in conversation into a short paragraph (60-100 words) capturing what the user said about how the day landed and anything they want to shape tomorrow with. Speak in third person about the user. Do NOT add commentary. Plain text only.`;

  const response = await callChatAPI({
    messages: [
      {
        role: 'user',
        content: `Day ${dayNumber} check-in transcript:\n\n${transcript}\n\nReturn the summary paragraph.`,
      },
    ],
    system,
    max_tokens: 400,
  });

  const summary = extractText(response).trim();

  return { dayNumber, summary, raw: transcript };
}
